import React from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Divider,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import PersonIcon from "@mui/icons-material/Person";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import ProfileImageWithFallback, {
  LIST_PROFILE_MAX_EDGE,
} from "../ProfileImageWithFallback/ProfileImageWithFallback";
import { dolorFormatAmount } from "../../Utils/Services";
import { formatAppVersion, formatPlatform } from "../../Utils/appVersionAdoptionUtils";

const getUserTypeLabel = (userType) => {
  if (userType === 0) return "Individual";
  if (userType === 1) return "Corporate";
  return "--";
};

const formatLastSeenDisplay = (customer) => {
  const lastSeen = customer?.last_seen;
  const lastActivity = customer?.last_activity;
  const effectiveDate = lastSeen || lastActivity;
  if (!effectiveDate) {
    return { label: "Never", color: "#9e9e9e", tooltip: "No activity recorded" };
  }

  const date = effectiveDate.toDate ? effectiveDate.toDate() : new Date(effectiveDate);
  const isFromTransaction = !lastSeen && lastActivity;
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  let label = "";
  if (diffMins < 5) label = "Online";
  else if (diffMins < 60) label = `${diffMins}m ago`;
  else if (diffHours < 24) label = `${diffHours}h ago`;
  else if (diffDays < 365) label = `${diffDays}d ago`;
  else label = `${Math.floor(diffDays / 365)}y ago`;

  let color = "#5fdba7";
  if (diffMins >= 5 && diffDays < 1) color = "#81c784";
  if (diffDays >= 1 && diffDays < 7) color = "#fff176";
  if (diffDays >= 7 && diffDays < 30) color = "#ff9800";
  if (diffDays >= 30) color = "#f44336";

  const fullDate = `${date.getDate().toString().padStart(2, "0")}/${(date.getMonth() + 1)
    .toString()
    .padStart(2, "0")}/${date.getFullYear()} ${date.getHours().toString().padStart(2, "0")}:${date
    .getMinutes()
    .toString()
    .padStart(2, "0")}`;
  const tooltip = isFromTransaction ? `${fullDate} (from last transaction)` : fullDate;

  return { label, color, tooltip };
};

const fieldLabelSx = {
  fontSize: "11px",
  fontWeight: 600,
  color: "text.secondary",
  letterSpacing: "0.04em",
};

const fieldValueSx = {
  fontSize: "13px",
  mt: 0.5,
  color: "text.primary",
};

const actionButtonSx = (bg, color, hoverBg) => ({
  background: bg,
  color,
  flex: 1,
  minWidth: "52px",
  height: "44px",
  borderRadius: "8px",
  "&:hover": {
    background: hoverBg,
  },
});

const CustomerCardView = ({
  customers,
  loading,
  onEdit,
  onView,
  onDelete,
  onApprove,
  onReject,
  onBlock,
  onUnblock,
  onResetYieldToggle,
  onViewLedgers,
  navigate,
  page,
}) => {
  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "200px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!customers || customers.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "200px",
          p: 3,
        }}
      >
        <Typography variant="body1" color="text.secondary">
          No customers found
        </Typography>
      </Box>
    );
  }

  const getStatusInfo = (status) => {
    switch (status) {
      case 0:
        return { label: "Pending", color: "#ff9800" };
      case 1:
        return { label: "Approved", color: "#4CAF50" };
      case 2:
        return { label: "Blocked", color: "#f44336" };
      case 3:
        return { label: "Blocked", color: "#f44336" };
      default:
        return { label: "Unknown", color: "#757575" };
    }
  };

  const renderActionButtons = (customer) => {
    const { status, doc_id, email, full_name } = customer;
    const userId = customer.id || doc_id || customer?.id;
    const buttons = [];

    if (status === 1) {
      buttons.push(
        <Tooltip title="Edit" key="edit">
          <IconButton
            onClick={() => onEdit && onEdit(customer)}
            sx={actionButtonSx("rgba(76, 175, 80, 0.25)", "#81C784", "rgba(76, 175, 80, 0.4)")}
          >
            <EditIcon sx={{ fontSize: "20px" }} />
          </IconButton>
        </Tooltip>,
        <Tooltip title="View" key="view">
          <IconButton
            onClick={() => onView && onView(customer)}
            sx={actionButtonSx("rgba(58, 222, 204, 0.22)", "#3adecc", "rgba(58, 222, 204, 0.38)")}
          >
            <RemoveRedEyeIcon sx={{ fontSize: "20px" }} />
          </IconButton>
        </Tooltip>,
        <Tooltip title="Block" key="block">
          <IconButton
            onClick={() => onBlock && onBlock(doc_id)}
            sx={actionButtonSx("rgba(244, 67, 54, 0.25)", "#ef5350", "rgba(244, 67, 54, 0.4)")}
          >
            <PersonIcon sx={{ fontSize: "20px" }} />
          </IconButton>
        </Tooltip>,
        <Tooltip title="Reset Yield Toggle" key="reset">
          <IconButton
            onClick={() => onResetYieldToggle && onResetYieldToggle(userId, email, full_name)}
            sx={actionButtonSx("rgba(255, 152, 0, 0.25)", "#ffb74d", "rgba(255, 152, 0, 0.4)")}
          >
            <RefreshIcon sx={{ fontSize: "20px" }} />
          </IconButton>
        </Tooltip>,
        <Tooltip title="Delete" key="delete">
          <IconButton
            onClick={() => onDelete && onDelete(userId, email, full_name)}
            sx={actionButtonSx("rgba(211, 47, 47, 0.25)", "#e57373", "rgba(211, 47, 47, 0.4)")}
          >
            <DeleteIcon sx={{ fontSize: "20px" }} />
          </IconButton>
        </Tooltip>
      );
    } else if (status === 2 || status === 3) {
      buttons.push(
        <Tooltip title="UnBlock" key="unblock">
          <IconButton
            onClick={() => onUnblock && onUnblock(doc_id)}
            sx={actionButtonSx("rgba(76, 175, 80, 0.25)", "#81C784", "rgba(76, 175, 80, 0.4)")}
          >
            <PersonOffIcon sx={{ fontSize: "20px" }} />
          </IconButton>
        </Tooltip>,
        <Tooltip title="View" key="view">
          <IconButton
            onClick={() => onView && onView(customer)}
            sx={actionButtonSx("rgba(58, 222, 204, 0.22)", "#3adecc", "rgba(58, 222, 204, 0.38)")}
          >
            <RemoveRedEyeIcon sx={{ fontSize: "20px" }} />
          </IconButton>
        </Tooltip>,
        <Tooltip title="Delete" key="delete">
          <IconButton
            onClick={() => onDelete && onDelete(userId, email, full_name)}
            sx={actionButtonSx("rgba(211, 47, 47, 0.25)", "#e57373", "rgba(211, 47, 47, 0.4)")}
          >
            <DeleteIcon sx={{ fontSize: "20px" }} />
          </IconButton>
        </Tooltip>
      );
    } else if (status === 0) {
      buttons.push(
        <Tooltip title="View" key="view">
          <IconButton
            onClick={() => onView && onView(customer)}
            sx={actionButtonSx("rgba(58, 222, 204, 0.22)", "#3adecc", "rgba(58, 222, 204, 0.38)")}
          >
            <RemoveRedEyeIcon sx={{ fontSize: "20px" }} />
          </IconButton>
        </Tooltip>,
        <Tooltip title="Approve" key="approve">
          <IconButton
            onClick={() => onApprove && onApprove(doc_id)}
            sx={actionButtonSx("rgba(76, 175, 80, 0.25)", "#81C784", "rgba(76, 175, 80, 0.4)")}
          >
            <CheckIcon sx={{ fontSize: "20px" }} />
          </IconButton>
        </Tooltip>,
        <Tooltip title="Reject" key="reject">
          <IconButton
            onClick={() => onReject && onReject(doc_id)}
            sx={actionButtonSx("rgba(244, 67, 54, 0.25)", "#ef5350", "rgba(244, 67, 54, 0.4)")}
          >
            <CloseIcon sx={{ fontSize: "20px" }} />
          </IconButton>
        </Tooltip>,
        <Tooltip title="Delete" key="delete">
          <IconButton
            onClick={() => onDelete && onDelete(userId, email, full_name)}
            sx={actionButtonSx("rgba(211, 47, 47, 0.25)", "#e57373", "rgba(211, 47, 47, 0.4)")}
          >
            <DeleteIcon sx={{ fontSize: "20px" }} />
          </IconButton>
        </Tooltip>
      );
    }

    return buttons;
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        p: 2,
        pb: 4,
      }}
    >
      {customers.map((customer) => {
        const statusInfo = getStatusInfo(customer?.status);
        const balance = parseFloat(customer?.net_balance || 0);

        return (
          <Card
            key={customer?.id || customer?.doc_id}
            sx={{
              width: "100%",
              bgcolor: "background.paper",
              border: "1px solid",
              borderColor: "divider",
              boxShadow: "0px 2px 8px rgba(0,0,0,0.25)",
              borderRadius: "12px",
              overflow: "visible",
            }}
          >
            <CardContent sx={{ p: 2.5 }}>
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  mb: 1.5,
                }}
              >
                <ProfileImageWithFallback
                  src={customer?.profile_image || customer?.profile_img}
                  alt={customer?.full_name || customer?.name || "Customer"}
                  width={60}
                  height={60}
                  borderRadius="50%"
                  iconColor="text.primary"
                  maxIntrinsicEdge={LIST_PROFILE_MAX_EDGE}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontSize: "18px",
                      fontWeight: 600,
                      mb: 0.5,
                      color: "text.primary",
                      fontFamily: "Poppins",
                      textTransform: "capitalize",
                    }}
                  >
                    {customer?.full_name || customer?.name || "Unknown"}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontSize: "14px", mb: 1, color: "text.secondary", wordBreak: "break-word" }}
                  >
                    {customer?.email || "--"}
                  </Typography>
                  <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", alignItems: "center" }}>
                    <Chip
                      label={statusInfo.label}
                      size="small"
                      sx={{
                        backgroundColor: statusInfo.color,
                        color: "#fff",
                        fontWeight: 500,
                        fontSize: "11px",
                        height: "24px",
                      }}
                    />
                    <Tooltip
                      title={
                        customer?.payout_mode === "fiat_manual"
                          ? "Paid in FIAT manually by Fiddux — Auto-Pay disabled, withdraws without crypto wallet."
                          : "Crypto withdrawal (wallet required, Auto-Pay eligible)."
                      }
                    >
                      <Chip
                        icon={<AccountBalanceWalletIcon sx={{ fontSize: 14 }} />}
                        label={customer?.payout_mode === "fiat_manual" ? "FIAT" : "Crypto"}
                        size="small"
                        color={customer?.payout_mode === "fiat_manual" ? "warning" : "success"}
                        variant="outlined"
                        sx={{ height: "24px" }}
                      />
                    </Tooltip>
                  </Box>
                </Box>
              </Box>

              <Divider sx={{ my: 1.5, borderColor: "divider" }} />

              <Box sx={{ mb: 1.5 }}>
                <Typography variant="caption" sx={fieldLabelSx}>
                  Balance
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: "20px",
                    fontWeight: 700,
                    color: balance >= 0 ? "success.light" : "error.light",
                    mt: 0.5,
                    fontFamily: "Poppins",
                  }}
                >
                  {dolorFormatAmount(balance)}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 1.5,
                  mb: 1.5,
                }}
              >
                <Box>
                  <Typography variant="caption" sx={fieldLabelSx}>
                    Type
                  </Typography>
                  <Typography variant="body2" sx={fieldValueSx}>
                    {getUserTypeLabel(customer?.user_type)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={fieldLabelSx}>
                    Last Seen
                  </Typography>
                  {(() => {
                    const lastSeen = formatLastSeenDisplay(customer);
                    return (
                      <Tooltip title={lastSeen.tooltip}>
                        <Typography
                          variant="body2"
                          sx={{
                            ...fieldValueSx,
                            color: lastSeen.color,
                            fontWeight: lastSeen.label === "Online" ? 600 : 400,
                          }}
                        >
                          {lastSeen.label}
                        </Typography>
                      </Tooltip>
                    );
                  })()}
                </Box>
                <Box sx={{ gridColumn: "1 / -1" }}>
                  <Typography variant="caption" sx={fieldLabelSx}>
                    App Version
                  </Typography>
                  <Typography variant="body2" sx={fieldValueSx}>
                    {customer?.app_build_number != null
                      ? `${formatAppVersion(customer)} · ${formatPlatform(customer)}`
                      : formatAppVersion(customer)}
                  </Typography>
                </Box>
              </Box>

              {customer?.registered_date && (
                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="caption" sx={fieldLabelSx}>
                    Registered
                  </Typography>
                  <Typography variant="body2" sx={fieldValueSx}>
                    {customer.registered_date?.toDate
                      ? customer.registered_date.toDate().toLocaleDateString()
                      : "--"}
                  </Typography>
                </Box>
              )}

              {(customer?.doc_id || customer?.id) && (
                <Box sx={{ mb: 1.5 }}>
                  <Typography
                    variant="caption"
                    sx={{ fontSize: "11px", fontWeight: 600, color: "text.secondary", letterSpacing: "0.04em" }}
                  >
                    User ID
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
                    <Typography
                      variant="body2"
                      sx={{ fontSize: "12px", fontFamily: "monospace", wordBreak: "break-all", color: "text.primary" }}
                    >
                      {customer?.doc_id || customer?.id}
                    </Typography>
                    <Tooltip title="Copy User ID">
                      <IconButton
                        size="small"
                        onClick={() => {
                          const uid = customer?.doc_id || customer?.id;
                          if (uid) navigator.clipboard.writeText(uid);
                        }}
                        sx={{ p: 0.25, color: "text.secondary" }}
                      >
                        <ContentCopyIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              )}

              <Divider sx={{ my: 1.5, borderColor: "divider" }} />

              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  flexWrap: "wrap",
                }}
              >
                {renderActionButtons(customer)}
              </Box>

              {customer?.doc_id && (
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<AccountBalanceIcon sx={{ fontSize: 18 }} />}
                  onClick={() => onViewLedgers && onViewLedgers(customer?.doc_id)}
                  sx={{
                    mt: 1.5,
                    height: "42px",
                    borderColor: "primary.light",
                    color: "primary.light",
                    textTransform: "none",
                    fontFamily: "Poppins",
                    fontWeight: 600,
                    fontSize: "14px",
                    "&:hover": {
                      borderColor: "primary.main",
                      color: "primary.main",
                      backgroundColor: "rgba(42, 245, 152, 0.08)",
                    },
                  }}
                >
                  View Ledgers
                </Button>
              )}
            </CardContent>
          </Card>
        );
      })}
    </Box>
  );
};

export default CustomerCardView;
